import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, TrendingUp, BarChart3, Users, Download, FileText } from 'lucide-react';
import { supabase, WeeklyLedger, LedgerEntry, EntryType, User } from '../lib/supabase';
import { exportToCSV } from '../lib/export';
import { translateEntryTypeName } from '../lib/translateEntryType';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

type ReportType = 'weekly' | 'monthly' | 'ytd' | 'comparison' | 'user-activity';

type LedgerWithDetails = WeeklyLedger & {
  user: User;
  entries: (LedgerEntry & { entry_type: EntryType })[];
};

export default function Reports() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [reportType, setReportType] = useState<ReportType>('weekly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ledgers, setLedgers] = useState<LedgerWithDetails[]>([]);
  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [loading, setLoading] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDayOfMonth.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
    loadEntryTypes();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      loadLedgers();
    }
  }, [startDate, endDate]);

  const loadEntryTypes = async () => {
    const { data } = await supabase
      .from('entry_types')
      .select('*')
      .order('display_order');
    if (data) setEntryTypes(data);
  };

  const loadLedgers = async () => {
    setLoading(true);
    const { data: ledgersData } = await supabase
      .from('weekly_ledgers')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });

    if (!ledgersData) {
      setLoading(false);
      return;
    }

    const { data: usersData } = await supabase.from('profiles').select('*');
    const { data: entriesData } = await supabase
      .from('ledger_entries')
      .select('*, entry_type:entry_types(*)');

    const ledgersWithDetails = ledgersData.map(ledger => {
      const user = usersData?.find(u => u.id === ledger.created_by);
      const entries = entriesData?.filter(e => e.weekly_ledger_id === ledger.id) || [];
      return { ...ledger, user: user!, entries };
    });

    setLedgers(ledgersWithDetails);
    setLoading(false);
  };

  const getWeeklySummary = () => {
    return ledgers.map(ledger => {
      const entryData: any = {
        [t('reports.date')]: new Date(ledger.date).toLocaleDateString(),
        [t('reports.user')]: ledger.user?.full_name || ledger.user?.email || 'Unknown'
      };

      entryTypes.forEach(type => {
        const entry = ledger.entries.find(e => e.entry_type_id === type.id);
        const translatedName = translateEntryTypeName(type.name, language);
        entryData[translatedName] = entry ? `£${Number(entry.amount).toFixed(2)}` : '£0.00';
      });

      const total = ledger.entries.reduce((sum, e) => sum + Number(e.amount), 0);
      entryData[t('reports.total')] = `£${total.toFixed(2)}`;

      return entryData;
    });
  };

  const getMonthlySummary = () => {
    const monthlyData: Record<string, any> = {};

    ledgers.forEach(ledger => {
      const monthKey = new Date(ledger.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { [t('reports.month')]: monthKey };
        entryTypes.forEach(type => {
          const translatedName = translateEntryTypeName(type.name, language);
          monthlyData[monthKey][translatedName] = 0;
        });
        monthlyData[monthKey][t('reports.total')] = 0;
      }

      ledger.entries.forEach(entry => {
        const translatedName = translateEntryTypeName(entry.entry_type.name, language);
        monthlyData[monthKey][translatedName] += Number(entry.amount);
        monthlyData[monthKey][t('reports.total')] += Number(entry.amount);
      });
    });

    return Object.values(monthlyData).map(month => {
      const formatted: any = { [t('reports.month')]: month[t('reports.month')] };
      entryTypes.forEach(type => {
        const translatedName = translateEntryTypeName(type.name, language);
        formatted[translatedName] = `£${(month[translatedName] || 0).toFixed(2)}`;
      });
      formatted[t('reports.total')] = `£${month[t('reports.total')].toFixed(2)}`;
      return formatted;
    });
  };

  const getYTDSummary = () => {
    const ytdData: any = { [t('reports.period')]: t('reports.yearToDateLabel') };

    entryTypes.forEach(type => {
      const translatedName = translateEntryTypeName(type.name, language);
      ytdData[translatedName] = 0;
    });
    ytdData[t('reports.total')] = 0;

    ledgers.forEach(ledger => {
      ledger.entries.forEach(entry => {
        const translatedName = translateEntryTypeName(entry.entry_type.name, language);
        ytdData[translatedName] += Number(entry.amount);
        ytdData[t('reports.total')] += Number(entry.amount);
      });
    });

    entryTypes.forEach(type => {
      const translatedName = translateEntryTypeName(type.name, language);
      ytdData[translatedName] = `£${ytdData[translatedName].toFixed(2)}`;
    });
    ytdData[t('reports.total')] = `£${ytdData[t('reports.total')].toFixed(2)}`;

    return [ytdData];
  };

  const getComparisonData = () => {
    const typeData: Record<string, any> = {};

    entryTypes.forEach(type => {
      const translatedName = translateEntryTypeName(type.name, language);
      typeData[type.name] = {
        [t('reports.entryType')]: translatedName,
        [t('reports.count')]: 0,
        [t('reports.total')]: 0,
        [t('reports.average')]: 0
      };
    });

    ledgers.forEach(ledger => {
      ledger.entries.forEach(entry => {
        const typeName = entry.entry_type.name;
        if (typeData[typeName]) {
          typeData[typeName][t('reports.count')]++;
          typeData[typeName][t('reports.total')] += Number(entry.amount);
        }
      });
    });

    return Object.values(typeData).map(data => ({
      [t('reports.entryType')]: data[t('reports.entryType')],
      [t('reports.count')]: data[t('reports.count')],
      [t('reports.total')]: `£${data[t('reports.total')].toFixed(2)}`,
      [t('reports.average')]: data[t('reports.count')] > 0 ? `£${(data[t('reports.total')] / data[t('reports.count')]).toFixed(2)}` : '£0.00'
    }));
  };

  const getUserActivity = () => {
    const userData: Record<string, any> = {};

    ledgers.forEach(ledger => {
      const userKey = ledger.user?.email || 'Unknown';
      if (!userData[userKey]) {
        userData[userKey] = {
          [t('reports.user')]: ledger.user?.full_name || userKey,
          [t('reports.email')]: userKey,
          [t('reports.ledgersCreated')]: 0,
          [t('reports.totalAmount')]: 0
        };
      }

      userData[userKey][t('reports.ledgersCreated')]++;
      const total = ledger.entries.reduce((sum, e) => sum + Number(e.amount), 0);
      userData[userKey][t('reports.totalAmount')] += total;
    });

    return Object.values(userData).map(data => ({
      ...data,
      [t('reports.totalAmount')]: `£${data[t('reports.totalAmount')].toFixed(2)}`
    }));
  };

  const getReportData = () => {
    switch (reportType) {
      case 'weekly':
        return getWeeklySummary();
      case 'monthly':
        return getMonthlySummary();
      case 'ytd':
        return getYTDSummary();
      case 'comparison':
        return getComparisonData();
      case 'user-activity':
        return getUserActivity();
      default:
        return [];
    }
  };

  const handleExportCSV = () => {
    const data = getReportData();
    const filename = `treasury-${reportType}-report-${new Date().toISOString().split('T')[0]}`;
    exportToCSV(data, filename);
  };


  const reportData = getReportData();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800">{t('reports.title')}</h1>
        <p className="text-slate-600 mt-1">{t('reports.subtitle')}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <button
            onClick={() => setReportType('weekly')}
            className={`p-4 rounded-lg border-2 transition-all ${
              reportType === 'weekly'
                ? 'border-emerald-600 bg-emerald-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <Calendar className={`w-6 h-6 mb-2 ${reportType === 'weekly' ? 'text-emerald-600' : 'text-slate-600'}`} />
            <p className={`font-medium ${reportType === 'weekly' ? 'text-emerald-900' : 'text-slate-800'}`}>
              {t('reports.weeklySummary')}
            </p>
          </button>

          <button
            onClick={() => setReportType('monthly')}
            className={`p-4 rounded-lg border-2 transition-all ${
              reportType === 'monthly'
                ? 'border-emerald-600 bg-emerald-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <BarChart3 className={`w-6 h-6 mb-2 ${reportType === 'monthly' ? 'text-emerald-600' : 'text-slate-600'}`} />
            <p className={`font-medium ${reportType === 'monthly' ? 'text-emerald-900' : 'text-slate-800'}`}>
              {t('reports.monthlySummary')}
            </p>
          </button>

          <button
            onClick={() => setReportType('ytd')}
            className={`p-4 rounded-lg border-2 transition-all ${
              reportType === 'ytd'
                ? 'border-emerald-600 bg-emerald-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <TrendingUp className={`w-6 h-6 mb-2 ${reportType === 'ytd' ? 'text-emerald-600' : 'text-slate-600'}`} />
            <p className={`font-medium ${reportType === 'ytd' ? 'text-emerald-900' : 'text-slate-800'}`}>
              {t('reports.yearToDate')}
            </p>
          </button>

          <button
            onClick={() => setReportType('comparison')}
            className={`p-4 rounded-lg border-2 transition-all ${
              reportType === 'comparison'
                ? 'border-emerald-600 bg-emerald-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <FileText className={`w-6 h-6 mb-2 ${reportType === 'comparison' ? 'text-emerald-600' : 'text-slate-600'}`} />
            <p className={`font-medium ${reportType === 'comparison' ? 'text-emerald-900' : 'text-slate-800'}`}>
              {t('reports.entryComparison')}
            </p>
          </button>

          {isAdmin && (
            <button
              onClick={() => setReportType('user-activity')}
              className={`p-4 rounded-lg border-2 transition-all ${
                reportType === 'user-activity'
                  ? 'border-emerald-600 bg-emerald-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Users className={`w-6 h-6 mb-2 ${reportType === 'user-activity' ? 'text-emerald-600' : 'text-slate-600'}`} />
              <p className={`font-medium ${reportType === 'user-activity' ? 'text-emerald-900' : 'text-slate-800'}`}>
                {t('reports.userActivity')}
              </p>
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('reports.startDate')}</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('reports.endDate')}</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mb-6">
          <button
            onClick={handleExportCSV}
            disabled={reportData.length === 0}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {t('reports.exportCSV')}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-600">{t('reports.loading')}</div>
        ) : reportData.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            {t('reports.noData')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  {Object.keys(reportData[0]).map((header) => (
                    <th key={header} className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b-2 border-slate-300">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    {Object.values(row).map((value, cellIdx) => (
                      <td key={cellIdx} className="px-4 py-3 text-sm text-slate-800 border-b border-slate-200">
                        {String(value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}